import React, { useEffect, useState, useRef } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Download, Archive, RefreshCw, UserPlus, MoreHorizontal, Check, Upload, Trash2, Users, UserCheck, Pause, FileEdit } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { ParticipantEditDialog } from '@/components/participants/profile/ParticipantEditDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { useAppDialog } from '@/hooks/useAppDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import SearchSelect from '@/components/common/SearchSelect';
import { STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';

const PARTICIPANT_STATUSES = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ON_HOLD', 'DISCHARGED'];

export default function ParticipantListPage() {
  const { dangerConfirm } = useAppDialog();
  const { user } = useAuth();
  const canDelete = ['Super Admin', 'Admin'].includes(user?.role_name);
  const canEdit = ['Super Admin', 'Admin'].includes(user?.role_name);
  const canCreate = (user?.permissions || []).includes('participants.manage');  // same permission POST /participants and import enforce
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [fundingFilter, setFundingFilter] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();   // NAV-1: dashboard deep link ?status=ACTIVE (existing backend `status` param)
  const statusParam = (searchParams.get('status') || '').toUpperCase();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  // Server-side pagination state (backend contract: page, pageSize, total)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, on_hold: 0, draft: 0 });
  const navigate = useNavigate();

  // Bulk operations state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatusDialog, setBulkStatusDialog] = useState(false);
  const [bulkArchiveDialog, setBulkArchiveDialog] = useState(false);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('ACTIVE');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkStaffId, setBulkStaffId] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const importInputRef = useRef(null);

  const fetchData = async () => {
    setPageLoading(true);
    try {
      const qp = new URLSearchParams();
      qp.set('page', String(page));
      qp.set('pageSize', String(pageSize));
      if (debouncedSearch) qp.set('search', debouncedSearch);
      if (fundingFilter) qp.set('fundingType', fundingFilter);
      if (statusParam) qp.set('status', statusParam);
      const [res, stRes, brRes] = await Promise.all([
        api.get(`/participants?${qp.toString()}`),
        api.get('/org/states'),
        api.get('/org/branches')
      ]);
      // API returns 'items' + 'total' for participants, 'data' for others
      setParticipants(res.data.items || res.data.data || []);
      setTotal(res.data.total ?? (res.data.items?.length || 0));
      setStates(stRes.data.data || []); 
      setBranches(brRes.data.data || []);

      // Dataset-wide status counts for KPI cards (independent of pagination)
      try {
        const sqp = new URLSearchParams();
        if (debouncedSearch) sqp.set('search', debouncedSearch);
        if (fundingFilter) sqp.set('fundingType', fundingFilter);
        const statsRes = await api.get(`/participants/stats?${sqp.toString()}`);
        setStats(statsRes.data.data || {});
      } catch { /* stats are best-effort */ }
      
      // Load staff for bulk assignment - separately with error handling
      try {
        // NEW-007: `page_size` was ignored (alias is pageSize, max 100) → only the first 20 users were listed
        const allUsers = await fetchAllPages('/admin/users');
        setStaff(allUsers.filter(u => u.status === 'ACTIVE' || u.is_active));
      } catch (staffErr) {
        console.warn('Could not load staff for bulk assignment:', staffErr);
      }
    } catch (e) { 
      console.error('Fetch error:', e);
      notify.error(null, 'LOAD_FAILED'); 
    } finally {
      setPageLoading(false);
    }
  };
  // Re-fetch when page, pageSize, debounced search or funding filter changes
  useEffect(() => { fetchData(); }, [page, pageSize, debouncedSearch, fundingFilter, statusParam]); // eslint-disable-line
  // Debounce free-text search into a server-side query; reset to page 1 on change
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);


  const handleDeleteParticipant = async (id, name, e) => {
    e.stopPropagation();
    const ok = await dangerConfirm({
      title: 'Archive participant?',
      description: `"${name}" will be archived and removed from active lists. The full record (notes, documents, clinical data, funding and audit history) is retained and can be restored later.`,
      confirmLabel: 'Archive participant',
      requireTypeToConfirm: 'ARCHIVE',
      reasonRequired: true,
      testId: 'participant-archive-confirm',
    });
    if (!ok) return;
    try {
      const res = await api.delete(`/participants/${id}`);
      const warnings = res.data?.warnings || [];
      if (warnings.length) {
        toast.warning(`Participant archived with ${warnings.length} active dependency warning(s): ${warnings.map(w => `${w.count} ${w.type}`).join(', ')}`);
      } else {
        toast.success('Participant archived');
      }
      // If we removed the only row on a page beyond the first, step back a page.
      if (participants.length === 1 && page > 1) {
        setPage(p => Math.max(1, p - 1));
      } else {
        fetchData();
      }
    } catch (err) {
      notify.error(err, 'ARCHIVE_FAILED');
    }
  };

  const openCreate = () => { setEditingParticipant(null); setDialogOpen(true); };

  const openEdit = (p, e) => {
    if (e) e.stopPropagation();
    setEditingParticipant(p);
    setDialogOpen(true);
  };

  // Shared canonical form (ParticipantEditDialog): edit → refresh list preserving filters/pagination; create → open profile
  const handleSaved = ({ mode, id }) => {
    if (mode === 'create') navigate(`/participants/${id}`);
    else fetchData();
  };

  // Search + funding are applied server-side; render the current page as-is.
  const filtered = participants;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleExportCSV = async () => {
    try {
      toast.info('Preparing export...');
      const response = await api.get('/participants/export/csv', {
        params: { search, status: null, fundingType: fundingFilter || null },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `participants_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      notify.error(null, 'EXPORT_FAILED');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/participants/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'participant_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download template');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      toast.info('Importing participants...');
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/participants/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data?.data || {};
      toast.success(`Import complete: ${d.created || 0} created, ${d.updated || 0} updated${(d.errors?.length) ? `, ${d.errors.length} errors` : ''}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Import failed');
    } finally {
      e.target.value = '';
    }
  };


  // Bulk selection handlers
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(p => p.id));
    }
  };

  const clearSelection = () => setSelectedIds([]);

  // Bulk operations handlers
  const handleBulkStatusChange = async () => {
    if (selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/participants/bulk/status-change', {
        participant_ids: selectedIds,
        new_status: bulkStatus,
        reason: bulkReason || null
      });
      const summary = res.data.summary;
      toast.success(`Status changed: ${summary.success} success, ${summary.failed} failed, ${summary.skipped} skipped`);
      setBulkStatusDialog(false);
      setBulkReason('');
      clearSelection();
      fetchData();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/participants/bulk/archive', {
        participant_ids: selectedIds,
        reason: bulkReason || null
      });
      const summary = res.data.summary;
      toast.success(`Archived: ${summary.success} success, ${summary.failed} failed, ${summary.skipped} skipped`);
      setBulkArchiveDialog(false);
      setBulkReason('');
      clearSelection();
      fetchData();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkAssignStaff = async () => {
    if (selectedIds.length === 0 || !bulkStaffId) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/participants/bulk/assign-staff', {
        participant_ids: selectedIds,
        staff_id: bulkStaffId,
        role_type: 'SUPPORT_WORKER',
        is_primary: false
      });
      const summary = res.data.summary;
      toast.success(`Staff assigned: ${summary.success} success, ${summary.failed} failed, ${summary.skipped} skipped`);
      setBulkAssignDialog(false);
      setBulkStaffId('');
      clearSelection();
      fetchData();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="participants-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Participants</h1>
          <p className="text-muted-foreground mt-1">Manage NDIS participants</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          {canCreate && (
            <>
              <Button variant="outline" onClick={handleDownloadTemplate} data-testid="import-template-btn">
                <Download className="h-4 w-4 mr-2" /> Template
              </Button>
              <input ref={importInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportFile} data-testid="import-file-input" />
              <Button variant="outline" onClick={() => importInputRef.current?.click()} data-testid="import-csv-btn">
                <Upload className="h-4 w-4 mr-2" /> Import
              </Button>
              <Button onClick={openCreate} data-testid="participant-create-button">
                <Plus className="h-4 w-4 mr-2" /> Add Participant
              </Button>
            </>
          )}
          <ParticipantEditDialog open={dialogOpen} onOpenChange={setDialogOpen} participant={editingParticipant} states={states} branches={branches} onSaved={handleSaved} />
        </div>
      </div>

      {/* KPI band — additive only, no backend change. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="participants-summary">
        <div className="rounded-lg border bg-white p-3" data-testid="participants-summary-total">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total</span>
            <Users className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900" data-testid="participants-total-value">{stats.total ?? total}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="participants-summary-active">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Active</span>
            <UserCheck className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-emerald-700" data-testid="participants-active-value">{stats.active ?? 0}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="participants-summary-on-hold">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">On hold</span>
            <Pause className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-amber-700" data-testid="participants-on-hold-value">{stats.on_hold ?? 0}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="participants-summary-draft">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Drafts</span>
            <FileEdit className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900" data-testid="participants-draft-value">{stats.draft ?? 0}</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="participant-search" />
        </div>
        {statusParam && (
          <Badge variant="outline" className="gap-1 cursor-pointer" onClick={() => { const n = new URLSearchParams(searchParams); n.delete('status'); setSearchParams(n, { replace: true }); setPage(1); }} title="Clear status filter" data-testid="participant-status-filter-chip">
            Status: {statusParam.charAt(0) + statusParam.slice(1).toLowerCase()} ×
          </Badge>
        )}
        <Select value={fundingFilter} onValueChange={(v) => { setFundingFilter(v === 'ALL' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="funding-type-filter">
            <SelectValue placeholder="All Funding Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Funding Types</SelectItem>
            <SelectItem value="SELF_MANAGED">Self Managed</SelectItem>
            <SelectItem value="NDIA_MANAGED">NDIA Managed</SelectItem>
            <SelectItem value="PLAN_MANAGED">Plan Managed</SelectItem>
            <SelectItem value="SIL_MANAGED">SIL Managed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-sm">
                  {selectedIds.length} selected
                </Badge>
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkStatusDialog(true)}
                  data-testid="bulk-status-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Change Status
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkAssignDialog(true)}
                  data-testid="bulk-assign-btn"
                >
                  <UserPlus className="h-4 w-4 mr-1" /> Assign Staff
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkArchiveDialog(true)}
                  className="text-amber-600 hover:text-amber-700"
                  data-testid="bulk-archive-btn"
                >
                  <Archive className="h-4 w-4 mr-1" /> Archive
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participants Table */}
      <Card>
        <CardContent className="p-0">
          {pageLoading ? (
            <TableSkeleton rows={6} columns={(canEdit || canDelete) ? 8 : 7} testid="participants-loading" />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Users}
              title={search || fundingFilter ? 'No participants match these filters' : 'No participants yet'}
              description={search || fundingFilter ? 'Try clearing search or funding-type filter.' : 'Add your first participant to start tracking NDIS services.'}
              action={!search && !fundingFilter && canCreate ? (
                <Button size="sm" onClick={openCreate} data-testid="participants-empty-add-button">
                  <Plus className="h-4 w-4 mr-1" />Add Participant
                </Button>
              ) : null}
              testid="participants-empty"
            />
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={filtered.length > 0 && selectedIds.length === filtered.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Preferred Name</TableHead>
                <TableHead>Participant ID</TableHead>
                <TableHead>NDIS Number</TableHead>
                <TableHead>DOA</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Funding Type</TableHead>
                <TableHead>Service Type</TableHead>
                <TableHead>Support Coordinator</TableHead>
                {(canEdit || canDelete) && <TableHead className="w-24"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => (
                <TableRow 
                  key={p.id} 
                  className={`hover:bg-muted/60 cursor-pointer ${selectedIds.includes(p.id) ? 'bg-primary/5' : ''}`}
                  data-testid={`participant-row-${p.id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.includes(p.id)}
                      onCheckedChange={(e) => toggleSelect(p.id, { stopPropagation: () => {} })}
                      onClick={(e) => toggleSelect(p.id, e)}
                      data-testid={`select-${p.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium" onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.first_name} {p.last_name}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.preferred_name || '-'}
                  </TableCell>
                  <TableCell className="font-mono text-xs" onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.participant_code || '-'}
                  </TableCell>
                  <TableCell className="font-mono text-xs" onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.ndis_number || '-'}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.date_of_admission || '-'}
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate" onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.address || '-'}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.state || p.state_id || '-'}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.phone || '-'}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.funding_type ? (
                      <span className="text-xs font-medium">{p.funding_type.replace(/_/g, ' ')}</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.service_type || '-'}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.support_coordinator_name || '-'}
                  </TableCell>
                  {(canEdit || canDelete) && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {canEdit && (
                          <button
                            onClick={(e) => openEdit(p, e)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/10 transition-colors"
                            title="Edit participant"
                            data-testid={`edit-participant-${p.id}`}
                          >
                            <FileEdit className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={(e) => handleDeleteParticipant(p.id, `${p.first_name} ${p.last_name}`, e)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                            title="Archive participant"
                            data-testid={`archive-participant-${p.id}`}
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination controls — server-side (page / pageSize / total) */}
      {!pageLoading && total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="participants-pagination">
          <div className="text-sm text-muted-foreground" data-testid="participants-range-count">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Per page</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20" data-testid="participants-page-size" aria-label="Rows per page"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="participants-prev-page"
                aria-label="Previous page"
              >
                Previous
              </Button>
              <span className="text-sm tabular-nums whitespace-nowrap" data-testid="participants-page-indicator" aria-live="polite">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline" size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="participants-next-page"
                aria-label="Next page"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
      <Dialog open={bulkStatusDialog} onOpenChange={setBulkStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Status Change</DialogTitle>
            <DialogDescription>
              Change the status for {selectedIds.length} selected participant(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger data-testid="bulk-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARTICIPANT_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Reason for status change..."
                data-testid="bulk-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkStatusChange} 
              disabled={bulkLoading}
              data-testid="confirm-bulk-status"
            >
              {bulkLoading ? 'Processing...' : 'Apply Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Archive Dialog */}
      <Dialog open={bulkArchiveDialog} onOpenChange={setBulkArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Archive</DialogTitle>
            <DialogDescription>
              Archive {selectedIds.length} selected participant(s). This action can be reversed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Reason for archiving..."
                data-testid="bulk-archive-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkArchiveDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={handleBulkArchive} 
              disabled={bulkLoading}
              data-testid="confirm-bulk-archive"
            >
              {bulkLoading ? 'Processing...' : 'Archive Participants'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Staff Dialog */}
      <Dialog open={bulkAssignDialog} onOpenChange={setBulkAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Assign Staff</DialogTitle>
            <DialogDescription>
              Assign a staff member to {selectedIds.length} selected participant(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Staff Member</Label>
              <SearchSelect
                options={staff.map(s => ({ value: s.id, label: `${s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim()} (${s.email})`, keywords: s.email || '' }))}
                value={bulkStaffId}
                onChange={setBulkStaffId}
                placeholder="Select a staff member..."
                searchPlaceholder={STAFF_SEARCH_HINT}
                emptyText="No staff match that search"
                testId="bulk-staff-select"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              The staff member will be added to the participant's support team as a Support Worker.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkAssignStaff} 
              disabled={bulkLoading || !bulkStaffId}
              data-testid="confirm-bulk-assign"
            >
              {bulkLoading ? 'Processing...' : 'Assign Staff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
